import { type FC, useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@/shared/components";
import { useBunnyAccountLibrariesLazyQuery } from "@/partner/entities/platforms";
import { BUNNY_REPLICATION_REGIONS } from "../model/bunnyReplicationRegions";

/** Значение радио «создать новую библиотеку». */
const CREATE_NEW = "";

interface BunnyAccountLibraryOption {
  id: string;
  name: string;
  videoCount: number;
}

interface BunnyAccountConnectFormProps {
  /** Имя платформы: попадает в подпись «создать новую библиотеку …». */
  platformTitle: string;
  /** Идёт ли подключение: карточка блокирует обе формы одним флагом. */
  connecting: boolean;
  /**
   * Bunny уже подключён и режим правки не открыт: форма только сообщает,
   * что подключение есть, ровно как заблокированная форма «Advanced».
   * Кнопка «Изменить ключи» живёт в карточке, она общая для двух режимов.
   */
  locked: boolean;
  /** Library ID текущего подключения: нужен предупреждению о замене. */
  currentLibraryId: string | null;
  /**
   * Библиотека, которую бекенд успел создать перед тем, как подключение
   * упало (`bunnyLibraryId` в extensions ошибки). Подставляется в выбор,
   * чтобы повтор доводил её, а не заводил в аккаунте вторую.
   */
  preselectLibraryId: string | null;
  onConnect: (input: {
    accountApiKey: string;
    libraryId: string | null;
    replicationRegions: string[];
  }) => Promise<void>;
  onError: (error: unknown) => void;
}

/**
 * Режим «Account API key»: партнёр вставляет один аккаунтный ключ, жмёт
 * «Load libraries» и выбирает, создать новую библиотеку или взять
 * существующую. Всё остальное (ключи библиотеки, хост pull zone, токен
 * подписи, вебхук) настраивает и сохраняет бекенд.
 *
 * Аккаунтный ключ живёт только в состоянии формы: он уходит в запрос
 * списка и в мутацию подключения, но нигде не показывается и не
 * подставляется из сохранённых настроек (их бекенд наружу не отдаёт).
 */
export const BunnyAccountConnectForm: FC<BunnyAccountConnectFormProps> = ({
  platformTitle,
  connecting,
  locked,
  currentLibraryId,
  preselectLibraryId,
  onConnect,
  onError,
}) => {
  const { t } = useTranslation();
  const [accountApiKey, setAccountApiKey] = useState("");
  const [libraries, setLibraries] = useState<
    BunnyAccountLibraryOption[] | null
  >(null);
  const [selectedLibraryId, setSelectedLibraryId] = useState(CREATE_NEW);
  const [replicationRegions, setReplicationRegions] = useState<string[]>([]);
  const [showCreated, setShowCreated] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [loadLibraries, { loading: loadingLibraries }] =
    useBunnyAccountLibrariesLazyQuery({
      onCompleted: (data) => {
        setLibraries([...data.bunnyAccountLibraries]);
        setSelectedLibraryId(CREATE_NEW);
        setShowCreated(false);
      },
      onError: (error) => {
        setLibraries(null);
        onError(error);
      },
    });

  // Библиотека создана, а подключение упало: список мог быть загружен до
  // создания, поэтому новой библиотеки в нём нет — добавляем её и выбираем.
  useEffect(() => {
    if (!preselectLibraryId) return;

    setLibraries((current) => {
      const known = current ?? [];
      return known.some((library) => library.id === preselectLibraryId)
        ? known
        : [
            ...known,
            { id: preselectLibraryId, name: platformTitle, videoCount: 0 },
          ];
    });
    setSelectedLibraryId(preselectLibraryId);
    setShowCreated(true);
  }, [preselectLibraryId, platformTitle]);

  const trimmedKey = accountApiKey.trim();
  const busy = connecting || loadingLibraries;
  const creatingNew = selectedLibraryId === CREATE_NEW;
  // Замена библиотеки у подключённой платформы: уже загруженные видео
  // останутся в старой, поэтому спрашиваем подтверждение. Повторное
  // подключение той же библиотеки только доводит её настройки.
  const replacesLibrary = Boolean(
    currentLibraryId && selectedLibraryId !== currentLibraryId
  );

  const submit = () =>
    onConnect({
      accountApiKey: trimmedKey,
      libraryId: creatingNew ? null : selectedLibraryId,
      replicationRegions: creatingNew ? replicationRegions : [],
    });

  if (locked) {
    return (
      <Alert severity="info">{t("bunnySettings.account.lockedHint")}</Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t("bunnySettings.account.hint")}
      </Typography>
      <TextField
        type="password"
        fullWidth
        autoComplete="off"
        label={t("bunnySettings.account.accountApiKeyLabel")}
        helperText={t("bunnySettings.account.accountApiKeyHelper")}
        value={accountApiKey}
        disabled={busy}
        onChange={(event) => {
          setAccountApiKey(event.target.value);
          // Список принадлежит конкретному ключу: сменили ключ — старые
          // библиотеки к нему отношения не имеют.
          setLibraries(null);
          setShowCreated(false);
        }}
      />
      <Button
        variant="outlined"
        sx={{ alignSelf: "flex-start" }}
        disabled={busy || !trimmedKey}
        startIcon={
          loadingLibraries ? <CircularProgress size={16} /> : undefined
        }
        onClick={() => {
          void loadLibraries({ variables: { accountApiKey: trimmedKey } });
        }}
      >
        {t("bunnySettings.account.loadLibraries")}
      </Button>

      {libraries && (
        <FormControl disabled={busy}>
          <FormLabel>{t("bunnySettings.account.libraryLabel")}</FormLabel>
          {libraries.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t("bunnySettings.account.noLibraries")}
            </Typography>
          )}
          <RadioGroup
            value={selectedLibraryId}
            onChange={(event) => setSelectedLibraryId(event.target.value)}
          >
            <FormControlLabel
              value={CREATE_NEW}
              control={<Radio />}
              label={t("bunnySettings.account.createNew", {
                title: platformTitle,
              })}
            />
            {libraries.map((library) => (
              <FormControlLabel
                key={library.id}
                value={library.id}
                control={<Radio />}
                label={t("bunnySettings.account.existingLibrary", {
                  name: library.name,
                  videoCount: library.videoCount,
                })}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {libraries && creatingNew && (
        <Stack spacing={1}>
          <FormLabel>{t("bunnySettings.account.regionsLabel")}</FormLabel>
          <ToggleButtonGroup
            size="small"
            value={replicationRegions}
            disabled={busy}
            onChange={(_event, value: string[]) => setReplicationRegions(value)}
          >
            {BUNNY_REPLICATION_REGIONS.map((region) => (
              <ToggleButton key={region} value={region}>
                {region}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {t("bunnySettings.account.regionsHelper")}
          </Typography>
        </Stack>
      )}

      {showCreated && (
        <Alert severity="info">
          {t("bunnySettings.account.createdPreselected")}
        </Alert>
      )}

      {/* Выбранная существующая библиотека переписывается под наш профиль,
          и защита ссылок на её CDN включается: если её видео отдаются
          неподписанными ссылками откуда-то ещё, они перестанут работать.
          Поэтому предупреждение показывается ровно на этом выборе. У
          библиотеки, которую только что создали мы сами (showCreated),
          ломать нечего, и там оно только путало бы. */}
      {libraries && !creatingNew && !showCreated && (
        <Alert severity="warning">
          {t("bunnySettings.account.existingWarning")}
        </Alert>
      )}

      {connecting && (
        <Alert severity="info" icon={<CircularProgress size={16} />}>
          {t("bunnySettings.account.connecting")}
        </Alert>
      )}

      <Button
        variant="contained"
        sx={{ alignSelf: "flex-end" }}
        // Подключение только после загрузки списка: иначе один промах по
        // кнопке заводил бы в аккаунте лишнюю библиотеку.
        disabled={busy || !trimmedKey || !libraries}
        onClick={() => {
          if (replacesLibrary) {
            setConfirmOpen(true);
            return;
          }
          void submit();
        }}
      >
        {t("bunnySettings.account.connect")}
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        loading={connecting}
        confirmTitle={t("bunnySettings.account.reconnectTitle")}
        confirmText={t("bunnySettings.account.reconnectText", {
          libraryId: currentLibraryId ?? "",
        })}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          void submit();
        }}
      />
    </Stack>
  );
};
